import {
  CAN_DO,
  COMPETITORS,
  DECK_SLIDES,
  DEMO_STORIES,
  ENABLEMENT,
  FAQ,
  FEATURE_CARDS,
  HERO,
  JOBS,
  PRICING_NOTES,
  SKUS,
} from '../marketingCopy';
import { AccessMapShot, ApprovalShot, SodShot } from '../ProductShots';

export function JobsPage({ navigate }) {
  const shots = [AccessMapShot, ApprovalShot, SodShot];
  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-3xl font-semibold text-orca-strong">Use cases</h1>
        <p className="mt-2 text-slate-400">The three stories every AE should be able to run without improvising.</p>
      </header>
      {JOBS.map((job, i) => {
        const Shot = shots[i];
        return (
          <article key={job.id} className="grid gap-6 rounded-2xl border border-orca bg-orca-panel p-6 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase text-cyan-400">{job.buyer}</p>
              <h2 className="mt-1 text-xl font-semibold text-orca-strong">{job.title}</h2>
              <p className="mt-3 text-sm text-slate-300">{job.outcome}</p>
              <p className="mt-2 text-sm text-slate-400">{job.story}</p>
            </div>
            <Shot />
          </article>
        );
      })}
      <button type="button" onClick={() => navigate('/demo-tour')} className="text-sm text-cyan-400">
        Open the guided tour →
      </button>
    </div>
  );
}

export function PricingPage({ navigate }) {
  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold text-orca-strong">Pricing</h1>
        <p className="mt-2 text-slate-400">
          We publish units, not a made-up sticker price. Ask for the current mid-market band.
        </p>
      </header>
      <div className="grid gap-4 md:grid-cols-3">
        {SKUS.map((sku) => (
          <article
            key={sku.id}
            className={`flex flex-col rounded-2xl border p-5 ${
              sku.featured ? 'border-cyan-600 bg-cyan-950/20' : 'border-orca bg-orca-panel'
            }`}
          >
            <h2 className="text-lg font-semibold text-orca-strong">{sku.name}</h2>
            <p className="mt-1 text-sm text-cyan-300">{sku.unit}</p>
            <p className="mt-2 text-sm text-slate-400">{sku.bestFor}</p>
            <ul className="mt-4 flex-1 space-y-1 text-sm text-slate-300">
              {sku.includes.map((line) => (
                <li key={line}>· {line}</li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => navigate(`/trial?sku=${sku.id}`)}
              className="mt-5 w-full rounded-lg bg-cyan-600 py-2 text-sm font-medium text-white hover:bg-cyan-500"
            >
              Start free trial
            </button>
          </article>
        ))}
      </div>
      <ul className="space-y-2 text-sm text-slate-400">
        {PRICING_NOTES.map((n) => (
          <li key={n}>· {n}</li>
        ))}
      </ul>
    </div>
  );
}

export function ComparePage() {
  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold text-orca-strong">How we compare</h1>
        <p className="mt-2 text-slate-400">Use this page in the first “why not Oracle / Pathlock?” email.</p>
      </header>
      <div className="overflow-hidden rounded-2xl border border-orca">
        <table className="w-full text-left text-sm">
          <thead className="bg-orca-raised text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-3 font-medium">Alternative</th>
              <th className="px-4 py-3 font-medium">They</th>
              <th className="px-4 py-3 font-medium">Orca AI</th>
            </tr>
          </thead>
          <tbody>
            {COMPETITORS.map((row) => (
              <tr key={row.name} className="border-t border-orca align-top">
                <td className="px-4 py-3 font-medium text-orca-strong">{row.name}</td>
                <td className="px-4 py-3 text-slate-400">{row.they}</td>
                <td className="px-4 py-3 text-slate-200">{row.we}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FaqPage({ navigate }) {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold text-orca-strong">FAQ</h1>
      {FAQ.map((item) => (
        <details key={item.q} className="rounded-xl border border-orca bg-orca-panel p-4">
          <summary className="cursor-pointer font-medium text-orca-strong">{item.q}</summary>
          <p className="mt-2 text-sm text-slate-300">{item.a}</p>
        </details>
      ))}
      <button type="button" onClick={() => navigate('/trust')} className="text-sm text-cyan-400">
        Read the data-flow note →
      </button>
    </div>
  );
}

export function DemoTourPage({ navigate }) {
  const shots = [AccessMapShot, ApprovalShot, SodShot];
  return (
    <div className="space-y-8">
      <header>
        <h1 className="text-3xl font-semibold text-orca-strong">Guided demo (3 stories)</h1>
        <p className="mt-2 text-slate-400">
          Always-on walkthrough when a live Fusion connection is not available. Target total: ~25 minutes including questions.
        </p>
      </header>
      {DEMO_STORIES.map((story, i) => {
        const Shot = shots[i];
        return (
          <section key={story.id} className="grid gap-6 rounded-2xl border border-orca bg-orca-panel p-6 lg:grid-cols-2">
            <div>
              <p className="text-xs text-cyan-400">
                Story {story.id} · {story.minutes} min
              </p>
              <h2 className="mt-1 text-xl font-semibold text-orca-strong">{story.title}</h2>
              <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-slate-300">
                {story.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
            </div>
            <Shot />
          </section>
        );
      })}
      <button
        type="button"
        onClick={() => navigate('/demo')}
        className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white"
      >
        Book this on your pod
      </button>
    </div>
  );
}

export function CollateralHome({ navigate }) {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold text-orca-strong">AE kit</h1>
      <p className="text-slate-400">Send these links. Print the one-pager. Present the 8-slide deck in the browser.</p>
      <ul className="grid gap-3 sm:grid-cols-2">
        {[
          ['/collateral/one-pager', 'One-pager (print)'],
          ['/collateral/demo-script', 'Demo script'],
          ['/collateral/enablement', 'Enablement / privileges'],
          ['/collateral/deck', '8-slide deck'],
          ['/collateral/video-script', '3-minute video script'],
          ['/compare', 'Competitive one-pager'],
        ].map(([href, label]) => (
          <li key={href}>
            <button
              type="button"
              onClick={() => navigate(href)}
              className="w-full rounded-xl border border-orca bg-orca-panel px-4 py-3 text-left text-sm font-medium hover:border-cyan-700"
            >
              {label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function OnePagerPage() {
  return (
    <article className="marketing-print space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orca-volt">{HERO.kicker}</p>
          <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-white">{HERO.headline}</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-300">{HERO.body}</p>
          <p className="mt-2 text-xs text-slate-500">{HERO.suiteNote}</p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="shrink-0 rounded-lg border border-white/20 px-3 py-1.5 text-xs text-slate-200"
        >
          Print / PDF
        </button>
      </div>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURE_CARDS.map((card) => (
          <article key={card.title} className="rounded-xl border border-white/10 bg-orca-mkt-card px-4 py-4">
            <h2 className="text-base font-semibold text-orca-gold">{card.title}</h2>
            <p className="mt-1 text-sm text-slate-400">{card.sub}</p>
          </article>
        ))}
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-orca-volt">What you can do</h2>
        <ul className="mt-3 grid gap-3 sm:grid-cols-2">
          {CAN_DO.map((item) => (
            <li key={item.title} className="rounded-xl border border-white/10 bg-orca-mkt-card p-4">
              <p className="text-sm font-semibold text-orca-gold">{item.title}</p>
              <p className="mt-1 text-sm text-slate-400">{item.do}</p>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-orca-volt">How it works</h2>
        <ul className="mt-3 grid gap-3 md:grid-cols-3">
          {JOBS.map((j) => (
            <li key={j.id} className="rounded-xl border border-white/10 bg-orca-mkt-card p-4">
              <p className="text-sm font-semibold text-white">{j.title}</p>
              <p className="mt-1 text-sm text-slate-400">{j.outcome}</p>
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-orca-volt">Packs</h2>
        <p className="mt-1 text-sm text-slate-400">Security / Approvals / Bundle · per Fusion pod / year · plus implementation.</p>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {SKUS.map((sku) => (
            <article key={sku.id} className="rounded-xl border border-white/10 bg-orca-mkt-card p-4">
              <h3 className="text-sm font-semibold text-white">{sku.name}</h3>
              <p className="mt-1 text-[11px] text-orca-volt">{sku.unit}</p>
            </article>
          ))}
        </div>
      </section>

      <p className="text-sm text-orca-volt">Start a free trial — or see it on your non-prod Fusion pod.</p>
    </article>
  );
}

export function DemoScriptPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold text-orca-strong">Demo script</h1>
      <p className="text-sm text-slate-400">Total 20–25 minutes. Do not improvise extra copilots.</p>
      <ol className="list-decimal space-y-2 pl-5 text-sm text-slate-300">
        <li>0:00 — Name the product and the not-CNAPP line. Confirm they have a Fusion HCM pod.</li>
        <li>1:00 — Connections: show Test (or say sandbox is pre-connected).</li>
        <li>2:00 — Story 1 access map (8 min). End on the blocking policy sentence.</li>
        <li>11:00 — Story 2 approval design + simulate (8 min). Do not deploy unless they ask.</li>
        <li>19:00 — Story 3 SoD + campaign (6 min). Mention revoke is explicit.</li>
        <li>25:00 — SKU + next step (book implementation or send one-pager).</li>
      </ol>
      {DEMO_STORIES.map((s) => (
        <section key={s.id} className="rounded-xl border border-orca bg-orca-panel p-4">
          <h2 className="font-semibold text-orca-strong">
            {s.id}. {s.title}
          </h2>
          <ol className="mt-2 list-decimal pl-5 text-sm text-slate-300">
            {s.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

export function EnablementPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold text-orca-strong">Customer enablement</h1>
      <Section title="Connection" items={ENABLEMENT.connection} />
      <Section title="Integration user privileges" items={ENABLEMENT.privileges} />
      <Section title="LLM" items={ENABLEMENT.llm} />
      <Section title="SSO" items={ENABLEMENT.sso} />
    </div>
  );
}

function Section({ title, items }) {
  return (
    <section className="rounded-xl border border-orca bg-orca-panel p-4">
      <h2 className="font-semibold text-orca-strong">{title}</h2>
      <ul className="mt-2 list-disc pl-5 text-sm text-slate-300">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </section>
  );
}

export function DeckPage() {
  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-semibold text-orca-strong">8-slide deck</h1>
      <p className="text-sm text-slate-400">Present full-screen. One slide per section.</p>
      <div className="grid gap-4 md:grid-cols-2">
        {DECK_SLIDES.map((s, i) => (
          <section key={s.title} className="flex min-h-[180px] flex-col rounded-2xl border border-orca bg-orca-panel p-6">
            <p className="text-xs text-cyan-400">Slide {i + 1}</p>
            <h2 className="mt-1 text-xl font-semibold text-orca-strong">{s.title}</h2>
            <p className="mt-3 flex-1 text-sm text-slate-300">{s.body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}

export function VideoScriptPage() {
  return (
    <div className="max-w-3xl space-y-4 text-sm text-slate-300">
      <h1 className="text-3xl font-semibold text-orca-strong">3-minute video script</h1>
      <p className="text-slate-400">
        Record against a connected sandbox when one exists. Until then, record over the guided tour shots.
      </p>
      <ol className="list-decimal space-y-3 pl-5">
        <li>
          <strong>0:00–0:20</strong> Face + logo. “Orca AI is a Fusion HCM workbench for security and approvals. We are not
          the CNAPP company.”
        </li>
        <li>
          <strong>0:20–1:10</strong> Access map. Jane. Blocking policy line.
        </li>
        <li>
          <strong>1:10–2:00</strong> Approval Designer. Promotion policy. Green simulation path.
        </li>
        <li>
          <strong>2:00–2:40</strong> SoD hit → campaign → revoke is explicit.
        </li>
        <li>
          <strong>2:40–3:00</strong> “Book 20 minutes on your pod. Per-pod annual SKUs. SLP Microsystems.”
        </li>
      </ol>
    </div>
  );
}
